/*
 * The invited team member's first screen (VISUAL.md §7.16, D-10 §2.3): what the product says to
 * somebody who has just redeemed an invitation and landed on "/".
 *
 * It answers the three questions of a first minute, each with a door — the rows are
 * `FirstRunRows.tsx`, which a guest's first screen draws too — and one line names the two keys that
 * reach everything else. The card around it — the rocket, the greeting and the foot with *Got it* —
 * is `FirstRunCard.tsx`, which the administrator's first day wears too. A guest's first screen is
 * this one with two sentences of its own (`FirstRunGuest.tsx`): the props `leadKey` and
 * `whereNoneKey` carry them.
 *
 * **Seen once.** The flag is `core.shell.seen_welcome`, a per-account setting and not a per-device
 * one, because the greeting belongs to the person: somebody who read it on their laptop has read it.
 * It is written *before* the screen closes and the write is quiet when it fails (`useShellSetting`),
 * so a bad minute on the server costs this tab a re-greeting on the next load at worst — never a
 * screen that cannot be dismissed.
 */

import { t, type PlainKey } from "../i18n/index.ts"
import { useModel } from "./context.tsx"
import { firstName } from "./firstName.ts"
import { FirstRunCard } from "./FirstRunCard.tsx"
import { FirstRunRows } from "./FirstRunRows.tsx"
import { useShellSetting } from "./preferences.tsx"
import type { PlaceDoor } from "./SetupChecklist.tsx"
import { MOD_KEY } from "./shortcuts.ts"

export function FirstRunMember({
	channel,
	compact = false,
	leadKey = "shell.firstRun.member.lead",
	whereNoneKey = "shell.firstRun.whereNone",
}: {
	channel?: PlaceDoor | undefined
	/** The block above the channel list on a narrow window: no illustration, a small mark beside the heading. */
	compact?: boolean
	/** The sentence under the greeting. */
	leadKey?: PlainKey
	/** What the first row says when the reader is in no channel yet (`FirstRunRows`). */
	whereNoneKey?: PlainKey
}) {
	// Writing the flag is also how the screen closes: the model carries the new value in the same
	// frame and Home stops asking for a greeting (routes/frames.tsx), so there is no second state.
	const [, setSeen] = useShellSetting("core.shell.seen_welcome")
	const name = useModel((model) => firstName(model.me?.displayName ?? ""))
	const dismiss = () => setSeen(true)

	return (
		<FirstRunCard
			title={t("shell.firstRun.member.title", { name })}
			lead={t(leadKey)}
			compact={compact}
			onDismiss={dismiss}
			hint={t("shell.firstRun.hint", { mod: MOD_KEY })}
		>
			<FirstRunRows channel={channel} whereNoneKey={whereNoneKey} onDismiss={dismiss} />
		</FirstRunCard>
	)
}
